import {
  PanelNames,
  WbDiffEditor,
  WbLogsViewer,
  WbMetadataTable,
  WbSpacer,
} from '@agilelab/plugin-wb-platform';
import { Content } from '@backstage/core-components';
import { Box, Paper } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import yaml from 'yaml';
import { CodeComparisonSkeleton } from '../wizard-common/CodeComparisonSkeleton';
import { AdditionalStep, getReviewData, Logs } from './utils';
import { memo, useMemo } from 'react';

const ReviewContent = memo(
  ({ formState, steps }: { formState: any; steps: Array<any> }) => {
    const reviewData = useMemo(
      () => getReviewData(formState, steps),
      [formState, steps],
    );
    return (
      <Content>
        <Paper
          style={{ backgroundColor: 'white', wordBreak: 'break-word' }}
          square
          elevation={0}
        >
          <WbMetadataTable metadata={reviewData} />

          <WbSpacer />
        </Paper>
      </Content>
    );
  },
);

export const reviewStep = (
  title: string,
  nextButtonText: string,
  formState: Record<string, any>,
  steps: Array<any>,
  handleFormReset: () => void,
  handleCreate: () => Promise<void>,
): AdditionalStep => ({
  title,
  component: <ReviewContent formState={formState} steps={steps} />,
  disableStepButtons: false,
  backButtonText: 'Back',
  nextButtonText: nextButtonText,
  analyticsEvent: 'create',
  resetButtonText: 'Reset',
  // Set the additional step to 1, which is the processing step
  onSaveButtonClick: ({ setAdditionalStep }) => {
    setAdditionalStep(1);
  },
  resetAction: handleFormReset,
  nextAction: handleCreate,
});

export const processingStep = (
  logs: Logs,
  isProcessingComplete: boolean,
  className: string,
  taskFailed: boolean,
  nextButtonText: string = 'Next step',
): AdditionalStep => ({
  title: 'Processing',
  component: (
    <Box className={className}>
      <WbLogsViewer {...logs} />
    </Box>
  ),
  disableStepButtons: !isProcessingComplete,
  customDisableStepButtons: { disableBack: false },
  backButtonText: 'Back',
  nextButtonText: nextButtonText,
  taskFailed,
});

export const comparisonStep = (options: {
  originalData: Record<string, any> | undefined;
  modifiedData: Record<string, any> | undefined;
  isLoading: boolean;
  classes: ClassNameMap<'button' | 'saveButton' | 'comparisonPageHeader'>;
  additionalActions: JSX.Element[];
  leftPanelTitle?: string;
  rightPanelTitle?: string;
  header?: JSX.Element;
}): AdditionalStep => ({
  title: 'Preview and Save',
  component: (
    <Box>
      {options.header}
      <PanelNames
        leftPanelTitle={options.leftPanelTitle ?? 'Source'}
        rightPanelTitle={options.rightPanelTitle ?? 'Modified'}
      />

      {options.isLoading ? (
        <CodeComparisonSkeleton height="calc(100vh - 305px)" />
      ) : (
        <WbDiffEditor
          height="calc(100vh - 305px)"
          width="100%"
          theme="diffEditorWizard"
          language="yaml"
          original={yaml.stringify(options.originalData)}
          modified={yaml.stringify(options.modifiedData)}
          options={{
            selectOnLineNumbers: true,
            contextmenu: false,
            readOnly: true,
            diffCodeLens: false,
            originalEditable: false,
            glyphMargin: true,
            linkedEditing: false,
            minimap: {
              enabled: false,
            },
          }}
        />
      )}
    </Box>
  ),
  additionalActions: options.additionalActions,
  resetButtonText: 'Start over',
  disableStepButtons: false,
});
